/**
 * @file: CreateOrderFab.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Create-order affordance for the layouts that have no bottom tab bar. The
 * strip — and with it the center "+" button — only renders on phone-sized
 * shells (`TabBar` bails on the same condition), so the wider shell would
 * otherwise offer no way to open an order at all. Sits with the AI assistant's
 * button, one slot above it.
 *
 * Mounted by the page, not the shell: only the two task boards (门店看板 /
 * 历史看板) stand for "the orders", so only they carry it. On a page with its
 * own "add" (a lead, a reservation) a second floating "+" would just be
 * ambiguous.
 */

import { useCreateOrder } from '@euler/app/flows/home/functions/useCreateOrder';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { LinearGradient } from 'expo-linear-gradient';
import { memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** Matches the tab bar's center button, so the two read as one affordance. */
const GRADIENT_COLORS = ['#009FD4', '#2669db'];

export const CreateOrderFab = memo(function CreateOrderFab() {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const isDesktop = useIsDesktopLayout();
  const createOrder = useCreateOrder();

  if (!isDesktop) return null;

  const buttonSize = rpx(52);
  // One slot above the AI assistant's button (which sits at
  // `insets.bottom + rpx(80)`, same size, same right margin). Fixed rather
  // than derived from whether that button is up: the AI FAB is per-page and
  // gets suppressed by sheets, and this one must not hop around with it.
  const bottom = insets.bottom + rpx(144);

  return (
    <View style={[styles.container, { right: rpx(16), bottom }]}>
      <View
        style={[
          styles.shadow,
          {
            width: buttonSize,
            height: buttonSize,
            borderRadius: buttonSize / 2,
          },
        ]}
      >
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={createOrder}
          // @ts-ignore react-native-web supports `title` for native tooltips
          title="创建订单"
        >
          <LinearGradient
            colors={GRADIENT_COLORS}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              styles.gradient,
              {
                width: buttonSize,
                height: buttonSize,
                borderRadius: buttonSize / 2,
              },
            ]}
          >
            <Icon name="plus" size={rpx(26)} color="#fff" />
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    zIndex: 999,
  },
  shadow: {
    shadowColor: '#0B4EA2',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.35,
    shadowRadius: 16,
    elevation: 24,
  },
  gradient: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
});
